import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAdmin } from '../hooks/useAdmin';
import BeeLogo from './BeeLogo';

interface HeaderProps {
  onToggleMobileMenu: () => void;
  mobileMenuOpen: boolean;
  onShowAccountManager: () => void;
  activePage: string;
  onNavigate: (page: any) => void;
}

const Header: React.FC<HeaderProps> = ({ 
  onToggleMobileMenu, 
  mobileMenuOpen, 
  onShowAccountManager,
  activePage,
  onNavigate 
}) => {
  const { user, logout } = useAuth();
  const { isAdmin } = useAdmin();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get user initials for avatar
  const getUserInitials = (name?: string) => {
    if (!name) return 'BK';
    const names = name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUserMenuOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleAccountManager = () => {
    onShowAccountManager();
    setUserMenuOpen(false);
  };

  return (
    <header className="header">
      <div className="header-container">
        <div className="logo" onClick={() => window.location.reload()} style={{cursor: 'pointer'}}>
          <BeeLogo variant="header" />
          <span>Beebase Pro</span>
        </div>

        {/* Desktop Navigation */}
        <nav className="desktop-nav">
          <button 
            className={`nav-item ${activePage === 'dashboard' ? 'active' : ''}`}
            onClick={() => onNavigate('dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`nav-item ${activePage === 'apiaries' ? 'active' : ''}`}
            onClick={() => onNavigate('apiaries')}
          >
            Apiaries
          </button>
          <button 
            className={`nav-item ${activePage === 'hives' ? 'active' : ''}`}
            onClick={() => onNavigate('hives')}
          >
            Hives
          </button>
          <button 
            className={`nav-item ${activePage === 'inspections' ? 'active' : ''}`}
            onClick={() => onNavigate('inspections')}
          >
            Inspections
          </button>
          <button 
            className={`nav-item ${activePage === 'export' ? 'active' : ''}`}
            onClick={() => onNavigate('export')}
          >
            Export Data
          </button>
          {isAdmin && (
            <button 
              className={`nav-item ${activePage === 'admin' ? 'active' : ''}`}
              onClick={() => onNavigate('admin')}
            >
              Admin
            </button>
          )}
        </nav>
        
        <div className="header-actions">
          <div className="notification-btn" onClick={() => onNavigate('alerts')}>
            <span>🔔</span>
            <span className="notification-badge">23</span>
          </div>
          
          <div className="user-menu-container" ref={userMenuRef}>
            <div className="user-menu" onClick={toggleUserMenu}>
              <div className="user-avatar">
                {user?.profileImageUrl ? (
                  <img 
                    src={user.profileImageUrl} 
                    alt={user.name || 'User'} 
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      // Fallback to initials if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.textContent = getUserInitials(user?.name);
                      }
                    }}
                  />
                ) : (
                  getUserInitials(user?.name)
                )}
              </div>
              <div className="user-info">
                <span className="user-name">{user?.name || 'Beekeeper'}</span>
                <span className="user-role">Pro Member</span>
              </div>
              <span>▼</span>
            </div>
            
            {userMenuOpen && (
              <div className="user-dropdown">
                <button className="dropdown-item" onClick={handleAccountManager}>
                  <span>👤</span>
                  Account Settings
                </button>
                <button className="dropdown-item logout" onClick={handleLogout}>
                  <span>🚪</span>
                  Sign Out
                </button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button 
            className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
            onClick={onToggleMobileMenu}
            aria-label="Toggle navigation menu"
          >
            ☰
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;